import { useEffect, useState, useContext } from "react";
import { useParams, Link } from "react-router-dom";
import { AuthContext } from "../context/AuthContext";

const API_BASE_URL = "https://warfc.com/api";

// =========================================================
// INFO BOX
// =========================================================

const InfoBox = ({ label, value }) => (
    <div className="flex justify-between border-b border-gray-800 py-2 gap-4">
        <span className="text-gray-400 font-medium">{label}:</span>
        <span className="text-white font-semibold text-right">
            {value}
        </span>
    </div>
);

// =========================================================
// FIGHTER CARD
// =========================================================

const FighterCard = ({
    fighter,
    isAdmin,
    isCoach,
    onAppointCoach,
    onRemoveCoach,
    onDelete,
    busy
}) => {
    const imageSource = fighter.image_url
        ? `${API_BASE_URL}${fighter.image_url}`
        : `https://ui-avatars.com/api/?name=${encodeURIComponent(
            `${fighter.name || ""} ${fighter.surname || ""}`
        )}&background=1f2937&color=d4af37`;

    return (
        <div className="relative p-4 bg-gray-800 rounded-lg shadow-lg hover:bg-gray-700 transition duration-300">

            {/* FIGHTER PROFILE LINK */}

            <Link
                to={`/fighters/${fighter.fighter_id}`}
                className="flex items-center space-x-4"
            >
                <img
                    src={imageSource}
                    alt={`${fighter.name} profile`}
                    className="w-16 h-16 object-cover rounded-full border-2 border-[#d4af37]"
                />

                <div className="flex-grow min-w-0">
                    <h3 className="text-lg font-bold text-white truncate">
                        {fighter.name} {fighter.surname}
                        {fighter.nickname &&
                            ` (${fighter.nickname})`}
                    </h3>

                    <p className="text-sm text-gray-400">
                        Record: {fighter.wins || 0}W -{" "}
                        {fighter.losses || 0}L
                    </p>
                </div>

                <span className="text-2xl text-[#d4af37]">
                    →
                </span>
            </Link>

            {/* ADMIN CONTROLS */}

            {isAdmin && (
                <div className="mt-3 flex gap-2">

                    {isCoach ? (
                        <button
                            type="button"
                            onClick={() => onRemoveCoach(fighter)}
                            disabled={busy}
                            className="flex-1 px-3 py-2 rounded-lg bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white text-sm font-semibold"
                        >
                            {busy
                                ? "Removing..."
                                : "Remove Coach"}
                        </button>
                    ) : (
                        <button
                            type="button"
                            onClick={() => onAppointCoach(fighter)}
                            disabled={busy}
                            className="flex-1 px-3 py-2 rounded-lg bg-[#d4af37] hover:bg-[#b8941f] disabled:opacity-50 text-black text-sm font-bold"
                        >
                            {busy
                                ? "Saving..."
                                : "Appoint as Coach"}
                        </button>
                    )}

                    <button
                        type="button"
                        onClick={() => onDelete(fighter)}
                        disabled={busy}
                        className="px-3 py-2 rounded-lg bg-red-900/60 hover:bg-red-900 disabled:opacity-50 text-red-200 text-sm"
                    >
                        Delete
                    </button>
                </div>
            )}
        </div>
    );
};

// =========================================================
// CLUB PROFILE
// =========================================================

export default function PublicClubProfile() {

    const { id } = useParams();
    const { user } = useContext(AuthContext);

    const isAdmin = user?.role === "admin";

    const token =
        user?.token ||
        localStorage.getItem("userToken");

    const [club, setClub] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [fighters, setFighters] = useState([]);

    // ID of fighter currently being modified
    const [busyId, setBusyId] = useState(null);

    // =====================================================
    // FETCH CLUB + ROSTER
    // =====================================================

    const fetchClubData = async () => {

        if (!id) {
            setLoading(false);
            setError("Invalid Club ID.");
            return;
        }

        try {

            setLoading(true);
            setError("");

            const [clubResponse, rosterResponse] =
                await Promise.all([
                    fetch(`${API_BASE_URL}/clubs/${id}`),
                    fetch(`${API_BASE_URL}/clubs/${id}/fighters`)
                ]);

            // ---------------- CLUB ----------------

            if (!clubResponse.ok) {

                const errorDetails =
                    await clubResponse
                        .json()
                        .catch(() => ({}));

                throw new Error(
                    errorDetails.message ||
                    errorDetails.error ||
                    `Failed to fetch club. Status: ${clubResponse.status}`
                );
            }

            const clubData =
                await clubResponse.json();

            setClub(clubData);

            // ---------------- ROSTER ----------------

            if (rosterResponse.ok) {

                const rosterData =
                    await rosterResponse.json();

                setFighters(
                    Array.isArray(rosterData)
                        ? rosterData
                        : []
                );

            } else {

                console.warn(
                    "Could not fetch club roster."
                );

                setFighters([]);
            }

        } catch (err) {

            console.error(
                "Club profile fetch error:",
                err
            );

            setError(
                err.message ||
                "An unexpected error occurred while loading the club profile."
            );

        } finally {

            setLoading(false);
        }
    };

    // =====================================================
    // INITIAL LOAD
    // =====================================================

    useEffect(() => {
        fetchClubData();
    }, [id]);

    // =====================================================
    // APPOINT COACH
    // =====================================================

    const handleAppointCoach = async (fighter) => {

        if (!isAdmin) return;

        const currentCoach =
            club?.coach_name;

        const confirmationMessage =
            currentCoach
                ? `${currentCoach} is currently the coach.\n\nAppoint ${fighter.name} ${fighter.surname} as the new coach?`
                : `Appoint ${fighter.name} ${fighter.surname} as the club coach?`;

        if (!window.confirm(confirmationMessage)) {
            return;
        }

        if (!token) {
            alert(
                "Authentication token not found. Please log in again."
            );
            return;
        }

        try {

            setBusyId(fighter.fighter_id);

            const response = await fetch(
                `${API_BASE_URL}/clubs/${id}/coach`,
                {
                    method: "POST",
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json"
                    },
                    body: JSON.stringify({
                        fighter_id:
                            fighter.fighter_id
                    })
                }
            );

            const data =
                await response
                    .json()
                    .catch(() => ({}));

            if (!response.ok) {

                throw new Error(
                    data.message ||
                    data.error ||
                    "Failed to appoint coach."
                );
            }

            // Reload club information
            await fetchClubData();

        } catch (err) {

            console.error(
                "Appoint coach error:",
                err
            );

            alert(
                err.message ||
                "Failed to appoint coach."
            );

        } finally {

            setBusyId(null);
        }
    };

    // =====================================================
    // REMOVE COACH
    // =====================================================

    const handleRemoveCoach = async (fighter) => {

        if (!isAdmin) return;

        if (
            !window.confirm(
                `Remove ${fighter.name} ${fighter.surname} as the club coach?`
            )
        ) {
            return;
        }

        if (!token) {
            alert(
                "Authentication token not found. Please log in again."
            );
            return;
        }

        try {

            setBusyId(fighter.fighter_id);

            const response = await fetch(
                `${API_BASE_URL}/clubs/${id}/coach`,
                {
                    method: "DELETE",
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                }
            );

            const data =
                await response
                    .json()
                    .catch(() => ({}));

            if (!response.ok) {

                throw new Error(
                    data.message ||
                    data.error ||
                    "Failed to remove coach."
                );
            }

            await fetchClubData();

        } catch (err) {

            console.error(
                "Remove coach error:",
                err
            );

            alert(
                err.message ||
                "Failed to remove coach."
            );

        } finally {

            setBusyId(null);
        }
    };

    // =====================================================
    // DELETE FIGHTER
    // =====================================================

    const handleDeleteFighter = async (fighter) => {

        if (!isAdmin) return;

        if (
            !window.confirm(
                `Are you sure you want to delete ${fighter.name} ${fighter.surname}?`
            )
        ) {
            return;
        }

        if (!token) {
            alert(
                "Authentication token not found. Please log in again."
            );
            return;
        }

        try {

            setBusyId(fighter.fighter_id);

            const response = await fetch(
                `${API_BASE_URL}/fighters/${fighter.fighter_id}`,
                {
                    method: "DELETE",
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json"
                    }
                }
            );

            const data =
                await response
                    .json()
                    .catch(() => ({}));

            if (!response.ok) {

                throw new Error(
                    data.message ||
                    data.error ||
                    `Failed to delete fighter (${response.status})`
                );
            }

            setFighters((prev) =>
                prev.filter(
                    (f) =>
                        f.fighter_id !==
                        fighter.fighter_id
                )
            );

            // If the deleted fighter was the coach,
            // refresh club information.
            if (
                club?.coach_fighter_id ===
                fighter.fighter_id
            ) {
                await fetchClubData();
            }

        } catch (err) {

            console.error(
                "Delete fighter error:",
                err
            );

            alert(
                err.message ||
                "An error occurred while deleting the fighter."
            );

        } finally {

            setBusyId(null);
        }
    };

    // =====================================================
    // LOADING
    // =====================================================

    if (loading) {

        return (
            <div className="text-white text-center py-20 text-xl">
                Loading club profile...
            </div>
        );
    }

    // =====================================================
    // ERROR
    // =====================================================

    if (error) {

        return (
            <div className="max-w-4xl mx-auto p-6 bg-red-800 text-white rounded-xl mt-10 text-center">
                Error: {error}
            </div>
        );
    }

    // =====================================================
    // NO CLUB
    // =====================================================

    if (!club) {

        return (
            <div className="max-w-4xl mx-auto p-6 bg-yellow-800 text-white rounded-xl mt-10 text-center">
                Club profile not available.
            </div>
        );
    }

    // =====================================================
    // CLUB LOGO
    // =====================================================

    const imageSource = club.logo_url
        ? `${API_BASE_URL}/uploads/clubs/${club.logo_url}`
        : `https://placehold.co/150x150/1f2937/d4af37?text=${encodeURIComponent(
            club.club_name
                .split(" ")
                .map((n) => n[0])
                .join("")
        )}`;

    // =====================================================
    // RENDER
    // =====================================================

    return (

        <div className="max-w-6xl mx-auto p-8 bg-black/30 backdrop-blur-md shadow-2xl rounded-xl mt-10 text-white">

            {/* =================================================
                HEADER
            ================================================= */}

            <header className="flex flex-col md:flex-row items-center md:items-start md:space-x-8 mb-8 border-b border-[#333] pb-6">

                <img
                    src={imageSource}
                    alt={`${club.club_name} Logo`}
                    className="w-32 h-32 object-cover rounded-full border-4 border-[#d4af37] mb-4 md:mb-0 shadow-lg"
                />

                <div className="text-center md:text-left">

                    <h1 className="text-4xl font-extrabold text-[#d4af37] mb-1">
                        {club.club_name}
                    </h1>

                    <p className="text-xl text-gray-300 mb-2">
                        Fighting Club since{" "}
                        {club.founded_year || "N/A"}
                    </p>

                    {/* CURRENT COACH */}

                    <p className="text-lg text-gray-400">

                        Head Coach:{" "}

                        <span className="text-white font-semibold">

                            {club.coach_name ||
                                club.trainer_name ||
                                "N/A"}

                        </span>

                    </p>

                    {club.coach_name && (

                        <div className="inline-block mt-2 px-3 py-1 rounded-full bg-[#d4af37]/15 border border-[#d4af37]/40 text-[#d4af37] text-sm">

                            Official WARFC Coach

                        </div>

                    )}

                </div>

            </header>

            {/* =================================================
                MAIN CONTENT
            ================================================= */}

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">

                {/* =================================================
                    CLUB INFORMATION
                ================================================= */}

                <div className="lg:col-span-1 space-y-6">

                    <h2 className="text-2xl font-bold text-gray-200 border-b border-gray-700 pb-2">
                        Contact & Details
                    </h2>

                    <InfoBox
                        label="Address"
                        value={
                            club.address ||
                            club.location ||
                            "N/A"
                        }
                    />

                    <InfoBox
                        label="Phone"
                        value={club.phone || "N/A"}
                    />

                    <InfoBox
                        label="Email"
                        value={club.email || "N/A"}
                    />

                    {club.website_url && (

                        <InfoBox
                            label="Website"
                            value={

                                <a
                                    href={
                                        club.website_url.startsWith(
                                            "http"
                                        )
                                            ? club.website_url
                                            : `https://${club.website_url}`
                                    }
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-blue-400 hover:text-blue-300 underline"
                                >
                                    Visit Site
                                </a>

                            }
                        />

                    )}

                    <h2 className="text-2xl font-bold text-gray-200 border-b border-gray-700 pb-2 pt-4">
                        About the Club
                    </h2>

                    <p className="text-gray-400 leading-relaxed text-sm">
                        {club.bio ||
                            "No detailed biography provided for this club yet."}
                    </p>

                </div>

                {/* =================================================
                    ROSTER
                ================================================= */}

                <div className="lg:col-span-2 space-y-6">

                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 border-b border-gray-700 pb-2">

                        <h2 className="text-2xl font-bold text-gray-200">
                            Official Roster ({fighters.length})
                        </h2>

                        {club.coach_name && (

                            <span className="text-sm text-[#d4af37]">
                                Coach: {club.coach_name}
                            </span>

                        )}

                    </div>

                    {/* ROSTER */}

                    {fighters.length > 0 ? (

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">

                            {fighters.map((fighter) => (

                                <FighterCard
                                    key={
                                        fighter.fighter_id
                                    }

                                    fighter={fighter}

                                    isAdmin={isAdmin}

                                    isCoach={
                                        Number(
                                            club.coach_fighter_id
                                        ) ===
                                        Number(
                                            fighter.fighter_id
                                        )
                                    }

                                    onAppointCoach={
                                        handleAppointCoach
                                    }

                                    onRemoveCoach={
                                        handleRemoveCoach
                                    }

                                    onDelete={
                                        handleDeleteFighter
                                    }

                                    busy={
                                        busyId ===
                                        fighter.fighter_id
                                    }
                                />

                            ))}

                        </div>

                    ) : (

                        <div className="p-6 bg-gray-800 text-gray-400 rounded-lg text-center">

                            No fighters currently listed on the roster.

                        </div>

                    )}

                </div>

            </div>

        </div>
    );
}